// Backend: GET /api/houses  POST /api/houses  PATCH /api/houses/:id

export type HouseStatus = 'available' | 'occupied' | 'cleaning' | 'disinfection' | 'maintenance' | 'inactive';
export type HousingSystem = 'deep_litter' | 'battery_cage' | 'free_range' | 'brooder' | 'breeder_house';

export interface PoultryHouse {
  id: string;
  houseCode: string;
  houseName: string;
  farmId: string;
  farmName: string;
  branchId: string | null;
  branchName: string | null;
  housingSystem: HousingSystem;
  status: HouseStatus;
  capacity: number;
  currentOccupancy: number;
  flockId: string | null;
  flockBatch: string | null;
  flockType: string | null;
  length: number; // metres
  width: number;  // metres
  ventilationType: string;
  waterSystem: string;
  feedingSystem: string;
  lastCleanedDate: string | null;
  lastDisinfectedDate: string | null;
  nextMaintenanceDate: string | null;
  manager: string;
  notes: string;
  createdAt: string;
}

export const HOUSING_SYSTEM_LABELS: Record<HousingSystem, string> = {
  deep_litter: 'Deep Litter',
  battery_cage: 'Battery Cage',
  free_range: 'Free Range',
  brooder: 'Brooder',
  breeder_house: 'Breeder House',
};

export const HOUSING_SYSTEM_COLORS: Record<HousingSystem, string> = {
  deep_litter: 'bg-amber-100 text-amber-700',
  battery_cage: 'bg-blue-100 text-blue-700',
  free_range: 'bg-teal-100 text-teal-700',
  brooder: 'bg-orange-100 text-orange-700',
  breeder_house: 'bg-purple-100 text-purple-700',
};

export const HOUSE_STATUS_LABELS: Record<HouseStatus, string> = {
  available: 'Available',
  occupied: 'Occupied',
  cleaning: 'Cleaning',
  disinfection: 'Disinfection',
  maintenance: 'Maintenance',
  inactive: 'Inactive',
};

export const HOUSE_STATUS_COLORS: Record<HouseStatus, string> = {
  available: 'bg-green-100 text-green-700',
  occupied: 'bg-blue-100 text-blue-700',
  cleaning: 'bg-yellow-100 text-yellow-700',
  disinfection: 'bg-orange-100 text-orange-700',
  maintenance: 'bg-red-100 text-red-700',
  inactive: 'bg-gray-100 text-gray-500',
};

export const MOCK_HOUSES: PoultryHouse[] = [
  {
    id: 'house-001',
    houseCode: 'IDF-H01',
    houseName: 'House 1 — Broiler A',
    farmId: 'farm-001',
    farmName: 'Idudi Poultry Farm',
    branchId: 'branch-001',
    branchName: 'Idudi Main Site',
    housingSystem: 'deep_litter',
    status: 'occupied',
    capacity: 5000,
    currentOccupancy: 4820,
    flockId: 'flock-001',
    flockBatch: 'BR-2026-001',
    flockType: 'Broiler',
    length: 60,
    width: 12,
    ventilationType: 'Natural cross-ventilation',
    waterSystem: 'Nipple drinkers',
    feedingSystem: 'Chain feeder',
    lastCleanedDate: '2026-07-15',
    lastDisinfectedDate: '2026-07-16',
    nextMaintenanceDate: '2026-10-01',
    manager: 'Sarah Namukasa',
    notes: 'High-performance broiler house. Litter replaced after each cycle.',
    createdAt: '2019-03-15',
  },
  {
    id: 'house-002',
    houseCode: 'IDF-H02',
    houseName: 'House 2 — Layer Block',
    farmId: 'farm-001',
    farmName: 'Idudi Poultry Farm',
    branchId: 'branch-001',
    branchName: 'Idudi Main Site',
    housingSystem: 'battery_cage',
    status: 'occupied',
    capacity: 4000,
    currentOccupancy: 3960,
    flockId: 'flock-002',
    flockBatch: 'LY-2026-001',
    flockType: 'Layer',
    length: 50,
    width: 10,
    ventilationType: 'Mechanical fans',
    waterSystem: 'Cup drinkers',
    feedingSystem: 'Trough feeder',
    lastCleanedDate: '2026-08-01',
    lastDisinfectedDate: '2026-08-02',
    nextMaintenanceDate: '2026-11-15',
    manager: 'Sarah Namukasa',
    notes: '4-tier battery cage system. 99% occupancy. Egg production stable.',
    createdAt: '2019-03-15',
  },
  {
    id: 'house-003',
    houseCode: 'IDF-H03',
    houseName: 'House 3 — Brooder Unit',
    farmId: 'farm-001',
    farmName: 'Idudi Poultry Farm',
    branchId: 'branch-001',
    branchName: 'Idudi Main Site',
    housingSystem: 'brooder',
    status: 'available',
    capacity: 2000,
    currentOccupancy: 0,
    flockId: null,
    flockBatch: null,
    flockType: null,
    length: 20,
    width: 10,
    ventilationType: 'Controlled temperature',
    waterSystem: 'Bell drinkers',
    feedingSystem: 'Chick trays',
    lastCleanedDate: '2026-08-20',
    lastDisinfectedDate: '2026-08-21',
    nextMaintenanceDate: null,
    manager: 'Peter Waiswa',
    notes: 'Ready for next chick intake. Brooder rings installed.',
    createdAt: '2020-05-10',
  },
  {
    id: 'house-004',
    houseCode: 'IDF-H04',
    houseName: 'House 4 — Breeder Block',
    farmId: 'farm-001',
    farmName: 'Idudi Poultry Farm',
    branchId: 'branch-002',
    branchName: 'Idudi Extension Site',
    housingSystem: 'breeder_house',
    status: 'occupied',
    capacity: 3000,
    currentOccupancy: 2800,
    flockId: 'flock-003',
    flockBatch: 'BRD-2026-001',
    flockType: 'Breeder',
    length: 40,
    width: 12,
    ventilationType: 'Natural + curtain sides',
    waterSystem: 'Nipple drinkers',
    feedingSystem: 'Pan feeder',
    lastCleanedDate: '2026-06-10',
    lastDisinfectedDate: '2026-06-12',
    nextMaintenanceDate: '2026-09-30',
    manager: 'Peter Waiswa',
    notes: 'Breeder flock. Egg collection twice daily.',
    createdAt: '2021-06-01',
  },
  {
    id: 'house-005',
    houseCode: 'IDF-H05',
    houseName: 'House 5 — Free Range Pen',
    farmId: 'farm-001',
    farmName: 'Idudi Poultry Farm',
    branchId: 'branch-002',
    branchName: 'Idudi Extension Site',
    housingSystem: 'free_range',
    status: 'cleaning',
    capacity: 1500,
    currentOccupancy: 0,
    flockId: null,
    flockBatch: null,
    flockType: null,
    length: 30,
    width: 15,
    ventilationType: 'Open-sided with mesh',
    waterSystem: 'Trough drinkers',
    feedingSystem: 'Scatter feeding',
    lastCleanedDate: null,
    lastDisinfectedDate: '2026-07-01',
    nextMaintenanceDate: null,
    manager: 'Peter Waiswa',
    notes: 'Cleaning in progress after cycle completion. Expected ready: 5 Sep 2026.',
    createdAt: '2021-06-01',
  },
  {
    id: 'house-006',
    houseCode: 'KDF-H01',
    houseName: 'House 1 — Kawanda Layer A',
    farmId: 'farm-002',
    farmName: 'Kampala Demonstration Farm',
    branchId: 'branch-003',
    branchName: 'Kawanda Layer Unit',
    housingSystem: 'battery_cage',
    status: 'occupied',
    capacity: 5000,
    currentOccupancy: 4950,
    flockId: 'flock-004',
    flockBatch: 'LY-2026-002',
    flockType: 'Layer',
    length: 60,
    width: 12,
    ventilationType: 'Mechanical fans + evaporative cooling',
    waterSystem: 'Nipple drinkers',
    feedingSystem: 'Automatic chain feeder',
    lastCleanedDate: '2026-07-20',
    lastDisinfectedDate: '2026-07-21',
    nextMaintenanceDate: '2026-12-01',
    manager: 'Dr. Grace Atim',
    notes: 'High-density demonstration layer house. 99% occupancy.',
    createdAt: '2022-01-10',
  },
  {
    id: 'house-007',
    houseCode: 'KDF-H02',
    houseName: 'House 2 — Kawanda Layer B',
    farmId: 'farm-002',
    farmName: 'Kampala Demonstration Farm',
    branchId: 'branch-003',
    branchName: 'Kawanda Layer Unit',
    housingSystem: 'battery_cage',
    status: 'disinfection',
    capacity: 4000,
    currentOccupancy: 0,
    flockId: null,
    flockBatch: null,
    flockType: null,
    length: 50,
    width: 10,
    ventilationType: 'Mechanical fans',
    waterSystem: 'Cup drinkers',
    feedingSystem: 'Trough feeder',
    lastCleanedDate: '2026-08-28',
    lastDisinfectedDate: null,
    nextMaintenanceDate: null,
    manager: 'Dr. Grace Atim',
    notes: 'Disinfection underway. Flock transferred to House 1 overflow.',
    createdAt: '2022-01-10',
  },
  {
    id: 'house-008',
    houseCode: 'KDF-H03',
    houseName: 'House 3 — Brooder & Grower',
    farmId: 'farm-002',
    farmName: 'Kampala Demonstration Farm',
    branchId: 'branch-003',
    branchName: 'Kawanda Layer Unit',
    housingSystem: 'brooder',
    status: 'maintenance',
    capacity: 3000,
    currentOccupancy: 0,
    flockId: null,
    flockBatch: null,
    flockType: null,
    length: 30,
    width: 10,
    ventilationType: 'Controlled temperature',
    waterSystem: 'Bell drinkers',
    feedingSystem: 'Chick trays',
    lastCleanedDate: '2026-08-10',
    lastDisinfectedDate: '2026-08-11',
    nextMaintenanceDate: '2026-09-10',
    manager: 'Dr. Grace Atim',
    notes: 'Roof repair and electrical maintenance in progress.',
    createdAt: '2022-03-01',
  },
  {
    id: 'house-009',
    houseCode: 'MBF-H01',
    houseName: 'House 1 — Broiler Block',
    farmId: 'farm-003',
    farmName: 'Mbarara Broiler Farm',
    branchId: null,
    branchName: null,
    housingSystem: 'deep_litter',
    status: 'inactive',
    capacity: 4000,
    currentOccupancy: 0,
    flockId: null,
    flockBatch: null,
    flockType: null,
    length: 50,
    width: 12,
    ventilationType: 'Natural cross-ventilation',
    waterSystem: 'Nipple drinkers',
    feedingSystem: 'Chain feeder',
    lastCleanedDate: '2026-01-15',
    lastDisinfectedDate: '2026-01-16',
    nextMaintenanceDate: null,
    manager: 'Alice Kyomugisha',
    notes: 'Farm currently inactive. House mothballed pending restocking decision.',
    createdAt: '2020-08-20',
  },
  {
    id: 'house-010',
    houseCode: 'MBF-H02',
    houseName: 'House 2 — Broiler Block',
    farmId: 'farm-003',
    farmName: 'Mbarara Broiler Farm',
    branchId: null,
    branchName: null,
    housingSystem: 'deep_litter',
    status: 'inactive',
    capacity: 4000,
    currentOccupancy: 0,
    flockId: null,
    flockBatch: null,
    flockType: null,
    length: 50,
    width: 12,
    ventilationType: 'Natural cross-ventilation',
    waterSystem: 'Nipple drinkers',
    feedingSystem: 'Chain feeder',
    lastCleanedDate: '2026-01-15',
    lastDisinfectedDate: '2026-01-16',
    nextMaintenanceDate: null,
    manager: 'Alice Kyomugisha',
    notes: 'Farm currently inactive.',
    createdAt: '2020-08-20',
  },
];
